/**
 * Design Tokens file parser.
 *
 * Supports TWO formats (auto-detected):
 *
 *  1. **W3C Design Tokens Community Group spec** (`.tokens.json`)
 *     - Tokens have `$type` and `$value` keys.
 *     - Colors: `{ "brand": { "primary": { "$type": "color", "$value": "#0066ff" } } }`
 *
 *  2. **Figma Tokens Studio** (legacy format, still very common)
 *     - Tokens have `type` and `value` keys (no `$` prefix).
 *     - Colors: `{ "brand": { "primary": { "type": "color", "value": "#0066ff" } } }`
 *
 * We flatten the nested structure into a dot-path key list and heuristically
 * map to our BrandKit.colors slots. If the heuristic fails, the admin UI
 * falls back to a manual mapping UI with the full key list.
 */

export interface FlatToken {
  /** Dot-path, e.g. "brand.primary" or "color.accent.500". */
  path: string;
  /** Token type — we only consume 'color'; others kept for future (spacing, radius, font). */
  type: string;
  /** Raw value, e.g. "#0066ff" for colors. */
  value: string;
}

export interface ParsedTokens {
  /** Detected format. */
  format: 'w3c' | 'figma-tokens-studio' | 'unknown';
  /** All color tokens, flattened. */
  colors: FlatToken[];
  /** All tokens regardless of type (for showing the user what was found). */
  all: FlatToken[];
  /** Heuristic best-guess mapping to BrandKit.colors slots (hex or unresolved). */
  suggested: Record<string, string | undefined>;
}

export interface TokensImportError {
  kind: 'parse-error' | 'no-colors-found' | 'invalid-format';
  message: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────

export async function parseTokensFile(file: File): Promise<ParsedTokens | TokensImportError> {
  let json: unknown;
  try {
    const text = await file.text();
    json = JSON.parse(text);
  } catch (e) {
    return { kind: 'parse-error', message: 'File is not valid JSON' };
  }

  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    return { kind: 'invalid-format', message: 'Top-level must be an object' };
  }

  const format = detectFormat(json as Record<string, unknown>);
  const all = flatten(json as Record<string, unknown>, format);
  const colors = all.filter((t) => t.type === 'color');

  if (colors.length === 0) {
    return {
      kind: 'no-colors-found',
      message: 'No color tokens found in file',
    };
  }

  const suggested = heuristicMap(colors);

  return { format, colors, all, suggested };
}

// ─────────────────────────────────────────────────────────────────────────────
// Format detection
// ─────────────────────────────────────────────────────────────────────────────

function detectFormat(obj: Record<string, unknown>): ParsedTokens['format'] {
  // Look for a token anywhere in the tree. First W3C-style, then figma-style.
  const hasKey = (key: string, node: unknown, depth = 0): boolean => {
    if (depth > 6 || !node || typeof node !== 'object') return false;
    const rec = node as Record<string, unknown>;
    if (rec[key] !== undefined) return true;
    for (const v of Object.values(rec)) {
      if (hasKey(key, v, depth + 1)) return true;
    }
    return false;
  };
  if (hasKey('$value', obj)) return 'w3c';
  if (hasKey('value', obj) && hasKey('type', obj)) return 'figma-tokens-studio';
  return 'unknown';
}

// ─────────────────────────────────────────────────────────────────────────────
// Flattening
// ─────────────────────────────────────────────────────────────────────────────

function flatten(obj: Record<string, unknown>, format: ParsedTokens['format']): FlatToken[] {
  const out: FlatToken[] = [];

  const valueKey = format === 'w3c' ? '$value' : 'value';
  const typeKey = format === 'w3c' ? '$type' : 'type';

  const walk = (node: Record<string, unknown>, path: string[]) => {
    const maybeValue = node[valueKey];
    const maybeType = node[typeKey];
    if (maybeValue !== undefined && typeof maybeValue === 'string') {
      out.push({
        path: path.join('.'),
        type: typeof maybeType === 'string' ? maybeType : 'unknown',
        value: maybeValue,
      });
      return;
    }
    for (const [key, child] of Object.entries(node)) {
      if (key.startsWith('$') && format !== 'w3c') continue; // e.g. $description
      if (child && typeof child === 'object' && !Array.isArray(child)) {
        walk(child as Record<string, unknown>, [...path, key]);
      }
    }
  };

  walk(obj, []);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Heuristic mapping — dot-paths → BrandKit.colors slots
// ─────────────────────────────────────────────────────────────────────────────

const SLOT_PATTERNS: Array<{
  slot: keyof BrandKitColors;
  patterns: RegExp[];
}> = [
  {
    slot: 'primary',
    patterns: [
      /\bprimary\b/i,
      /\bbrand\b.*\bprimary\b/i,
      /\bbrand\b$/i,
      /\baccent\b.*\b(500|600|default)\b/i,
    ],
  },
  {
    slot: 'accent',
    patterns: [/\baccent\b/i, /\bsecondary\b/i, /\btertiary\b/i],
  },
  {
    slot: 'background',
    patterns: [/\bbackground\b/i, /\bbg\b/i, /\bsurface\b.*\bdefault\b/i, /\bpage\b/i],
  },
  {
    slot: 'surface',
    patterns: [/\bsurface\b/i, /\bcard\b/i, /\bpanel\b/i],
  },
  {
    slot: 'text',
    patterns: [/\btext\b.*\b(default|primary)\b/i, /\bforeground\b/i, /\bfg\b/i, /^text$/i],
  },
  {
    slot: 'textMuted',
    patterns: [/\btext\b.*\b(muted|secondary|subtle)\b/i, /\bmuted\b/i],
  },
  {
    slot: 'border',
    patterns: [/\bborder\b/i, /\bdivider\b/i, /\bline\b/i],
  },
  {
    slot: 'success',
    patterns: [/\bsuccess\b/i, /\bpositive\b/i, /\bgreen\b/i],
  },
  {
    slot: 'warning',
    patterns: [/\bwarning\b/i, /\bcaution\b/i, /\byellow\b/i, /\bamber\b/i, /\borange\b/i],
  },
  {
    slot: 'danger',
    patterns: [/\bdanger\b/i, /\berror\b/i, /\bnegative\b/i, /\bred\b/i],
  },
  {
    slot: 'primaryContrast',
    patterns: [/\bprimary.*contrast\b/i, /\bon[-_.]?primary\b/i],
  },
];

interface BrandKitColors {
  primary: string;
  primaryContrast: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  border: string;
  success: string;
  warning: string;
  danger: string;
}

function heuristicMap(colors: FlatToken[]): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};

  for (const { slot, patterns } of SLOT_PATTERNS) {
    // First match wins for each slot.
    const match = colors.find((c) => patterns.some((p) => p.test(c.path)));
    if (match && isValidCssColor(match.value)) {
      out[slot] = normaliseColor(match.value);
    }
  }

  return out;
}

function isValidCssColor(s: string): boolean {
  // Accept hex, hsl, rgb, oklch, named. Reject empty / non-strings.
  return typeof s === 'string' && s.length > 0 && s.length < 60;
}

function normaliseColor(s: string): string {
  // Keep as-is for now. We could normalise hsl() → #hex, but that loses alpha.
  return s.trim();
}
